/**
 * ParentCategorySelector Component
 * 
 * Dropdown selector for parent category with hierarchical display
 * Used by CategoryForm
 */

import React, { useMemo } from 'react';
import { Category, CategoryType } from '../../../../types/category.types';

interface ParentCategorySelectorProps {
  value?: string;
  onChange: (parentId?: string) => void;
  categories: Category[];
  categoryType: CategoryType;
  excludeCategoryId?: string; // Exclude this category from options (to prevent circular references)
  error?: string;
}

export function ParentCategorySelector({
  value,
  onChange,
  categories,
  categoryType,
  excludeCategoryId,
  error
}: ParentCategorySelectorProps): React.ReactElement {
  // Filter categories by type, exclude the current category, and only show top-level categories (no parent_id)
  const availableCategories = useMemo(() => {
    return categories.filter(
      cat =>
        cat.type === categoryType &&
        cat.id !== excludeCategoryId &&
        !cat.parentId // Only show top-level categories (categories without a parent)
    );
  }, [categories, categoryType, excludeCategoryId]);

  return (
    <div>
      <label htmlFor="parent-category" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Parent Category
      </label>
      <select
        id="parent-category"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        className={`mt-1 block w-full rounded-lg border ${
          error
            ? 'border-red-300 dark:border-red-700'
            : 'border-gray-300 dark:border-gray-600'
        } bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
        aria-invalid={!!error}
        aria-describedby={error ? 'parent-category-error' : undefined}
      >
        <option value="">Select a parent category to create a subcategory</option>
        {availableCategories.map(cat => (
          <option key={cat.id} value={cat.id}>
            {cat.name}
          </option>
        ))}
      </select>
      {error && (
        <p id="parent-category-error" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

